/** Hub vs Branch location types (legacy middle_destination reads as Hub). */

export function normalizeDestinationType(type) {
  if (!type) return type;
  const value = String(type).trim().toLowerCase();
  if (value === 'middle_destination') return 'hub_destination';
  return value;
}

export function destinationTypeLabel(type) {
  const normalized = normalizeDestinationType(type);
  if (normalized === 'hub_destination') return 'Hub';
  if (normalized === 'branch_destination') return 'Branch';
  return type ? String(type).replace(/_/g, ' ') : '';
}

export function isHubDestination(type) {
  return normalizeDestinationType(type) === 'hub_destination';
}

export function isBranchDestination(type) {
  return normalizeDestinationType(type) === 'branch_destination';
}

/** Strip legacy "Hub - Branch" prefix; return branch/location name only. */
export function stripHubPrefixFromLabel(label) {
  if (label == null) return '';
  const name = String(label).trim();
  if (!name) return '';
  if (name.includes(' - ')) {
    const branch = name.split(' - ').slice(1).join(' - ').trim();
    return branch || name;
  }
  return name;
}

/** Display / stored label: destination name only (hub or branch). */
export function formatHubBranchLabel(hub, _hubsById = null) {
  if (!hub) return '';
  const branchName = (hub.location || hub.hub_name || hub.name || '').trim();
  if (branchName) return branchName;
  return stripHubPrefixFromLabel(hub.display_label);
}

/** User/agent dropdown label: "Name (Location)". Other reports keep this format. */
export function formatUserWithLocation(user, hubsById = null) {
  if (!user) return '';
  const name = (user.full_name || user.name || user.username || user.agent_name || '').trim();
  if (!name) return '';

  let loc = '';
  const hubId = user.location_hub_id ?? user.hub_id ?? null;
  if (hubId != null && hubsById) {
    const hub = hubsById[hubId] || hubsById[String(hubId)];
    if (hub) loc = formatHubBranchLabel(hub, hubsById);
  }
  if (!loc) {
    loc = stripHubPrefixFromLabel(
      user.location_hub_label ||
        user.location_hub_name ||
        user.location_label ||
        user.location ||
        user.city ||
        '',
    );
  }

  return loc ? `${name} (${loc})` : name;
}

function userRoleName(user) {
  if (!user) return '';
  if (typeof user.role === 'string') return user.role.trim();
  return String(user.role_name || user.role?.role_name || '').trim();
}

function userHubLabel(user, hubsById = null) {
  if (!user) return '';
  const hubId = user.location_hub_id ?? user.hub_id ?? null;
  if (hubId != null && hubsById) {
    const hub = hubsById[hubId] || hubsById[String(hubId)];
    if (hub) return formatHubBranchLabel(hub, hubsById);
  }
  return stripHubPrefixFromLabel(
    user.location_hub_label ||
      user.location_hub_name ||
      user.location_label ||
      user.location ||
      user.city ||
      '',
  );
}

/** Daily Branch user filter: "Full name - Role - Location hub". */
export function formatUserNameRoleHub(user, hubsById = null) {
  if (!user) return '';
  const name = (user.full_name || user.name || user.username || user.agent_name || '').trim();
  if (!name) return '';
  const role = userRoleName(user);
  const hub = userHubLabel(user, hubsById);
  return [name, role, hub].filter(Boolean).join(' - ');
}

/** GC Number prefix from a single hub (legacy / fallback). */
export function getGcPrefixCodes(hub) {
  if (!hub) return { hubCode: null, branchCode: null, prefix: null };
  const code = (hub.location_code || '').trim().toUpperCase() || null;
  return {
    hubCode: code,
    branchCode: code,
    prefix: code,
  };
}

/** GC Number prefix from sender + receiver location codes → "HYD-MP". */
export function getGcRoutePrefix(senderHub, receiverHub) {
  const hubCode = (senderHub?.location_code || '').trim().toUpperCase() || null;
  const branchCode =
    (receiverHub?.location_code || '').trim().toUpperCase() || hubCode;
  if (!hubCode && !branchCode) {
    return { hubCode: null, branchCode: null, prefix: null };
  }
  return {
    hubCode,
    branchCode,
    prefix: hubCode && branchCode ? `${hubCode}-${branchCode}` : hubCode || branchCode,
  };
}

/** GC payment suffix: PD (paid), TP (to pay), CRD (credit). */
export function getGcPaymentCode(modeOfPayment) {
  const key = String(modeOfPayment || '').trim().toLowerCase();
  if (key === 'to_pay') return 'TP';
  if (key === 'credit') return 'CRD';
  return 'PD';
}

/** Two-digit year for GC Number, e.g. 2026 → "26". */
export function getGcYearSuffix(date = new Date()) {
  return String(date.getFullYear()).slice(-2);
}

/** Payment + year tokens: PD and 26 → used as SENDER-RECEIVER-PD-26-SEQ. */
export function getGcPaymentYearSuffix(modeOfPayment, date = new Date()) {
  return `${getGcPaymentCode(modeOfPayment)}-${getGcYearSuffix(date)}`;
}

/** Keep only branch destinations. */
export function filterBranchLocations(hubs = []) {
  return (Array.isArray(hubs) ? hubs : []).filter((h) =>
    isBranchDestination(h?.destination_type),
  );
}

export function filterHubLocations(hubs = []) {
  return (Array.isArray(hubs) ? hubs : []).filter((h) =>
    isHubDestination(h?.destination_type),
  );
}

/** Hub + branch destinations for shipment From/To pickers. */
export function filterShipmentLocations(hubs = []) {
  return (Array.isArray(hubs) ? hubs : []).filter(
    (h) =>
      isBranchDestination(h?.destination_type) ||
      isHubDestination(h?.destination_type),
  );
}

export function hubRecordId(hub) {
  if (!hub) return '';
  const id = hub.id ?? hub.hub_id;
  return id == null || id === '' ? '' : String(id);
}

/** null/undefined = unrestricted; otherwise a Set of allowed destination hub ids. */
export function normalizeDestinationHubIds(raw) {
  if (raw == null) return null;
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.map((id) => String(id)).filter((id) => id && id !== 'null' && id !== 'undefined'));
}

export function isAllowedBookingDestination(senderHub, receiverHub) {
  if (!receiverHub) return false;
  const senderId = hubRecordId(senderHub);
  const receiverId = hubRecordId(receiverHub);
  if (!receiverId) return false;
  if (senderId && senderId === receiverId) return false;
  const allowed = normalizeDestinationHubIds(senderHub?.destination_location_hub_ids);
  if (allowed == null) return true;
  return allowed.has(receiverId);
}

export function filterAllowedBookingDestinations(hubs = [], senderHub = null) {
  return (Array.isArray(hubs) ? hubs : []).filter((hub) =>
    isAllowedBookingDestination(senderHub, hub),
  );
}
